import { useState } from "react";
import type { TrainingMetrics } from "../../api/ml";
import { BAND_LABEL, summarizeResults, type Band } from "../../lib/results";

const ICON: Record<Band, string> = { good: "✓", usable: "◐", weak: "✗" };
const BAND_COLOR: Record<Band, string> = { good: "var(--success)", usable: "var(--warning)", weak: "var(--danger-text)" };

/** "✓ Good": status colour always with its icon and word, never colour alone. */
const BandTag = ({ b }: { b: Band }) => (
  <span style={{ color: BAND_COLOR[b], fontWeight: 600, whiteSpace: "nowrap" }}>{ICON[b]} {BAND_LABEL[b]}</span>
);

// Chart geometry, in viewBox units; the SVG scales to the panel's width.
const W = 560, H = 180, L = 34, R = 12, T = 12, B = 26;
const TICKS = [0, 0.25, 0.5, 0.75, 1];

/** The validation score over the run: one line (mAP50), the best epoch marked, a tooltip per point. */
function ScoreChart({ points, best, totalEpochs }: NonNullable<ReturnType<typeof summarizeResults>>) {
  const [hover, setHover] = useState<number | null>(null);
  const maxEpoch = Math.max(totalEpochs, points[points.length - 1].epoch, 1);
  const x = (e: number) => L + (e / maxEpoch) * (W - L - R);
  const y = (v: number) => T + (1 - v) * (H - T - B);
  const path = points.map((p, i) => `${i ? "L" : "M"}${x(p.epoch).toFixed(1)},${y(p.map50).toFixed(1)}`).join(" ");
  const h = hover === null ? null : points[hover];
  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" data-testid="results-chart"
        aria-label={`Validation mAP50 by epoch; best ${best.map50.toFixed(2)} at epoch ${best.epoch}`}>
        {TICKS.map((t) => (
          <g key={t}>
            <line x1={L} x2={W - R} y1={y(t)} y2={y(t)} stroke="var(--border)" strokeWidth={t === 0 ? 1 : 0.5} />
            <text x={L - 6} y={y(t) + 3} textAnchor="end" fontSize={10} fill="var(--muted)">{t.toFixed(2)}</text>
          </g>
        ))}
        <text x={W - R} y={H - 6} textAnchor="end" fontSize={10} fill="var(--muted)">epoch {maxEpoch}</text>
        <text x={L} y={H - 6} fontSize={10} fill="var(--muted)">epoch 0</text>
        <path d={path} fill="none" stroke="var(--accent)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <circle key={p.epoch} cx={x(p.epoch)} cy={y(p.map50)} r={p === best ? 5 : 4}
            fill={p === best ? "var(--accent)" : "var(--surface)"} stroke={p === best ? "var(--surface)" : "var(--accent)"} strokeWidth={2}
            opacity={hover === null || hover === i ? 1 : 0.6} />
        ))}
        <text x={x(best.epoch)} y={Math.max(T + 8, y(best.map50) - 10)} textAnchor={x(best.epoch) > W - 120 ? "end" : "middle"}
          fontSize={11} fontWeight={600} fill="var(--text)">best {best.map50.toFixed(2)}</text>
        {/* Hit areas wider than the marks: one column per validated epoch. */}
        {points.map((p, i) => {
          const prev = i ? x(points[i - 1].epoch) : L, next = i < points.length - 1 ? x(points[i + 1].epoch) : W - R;
          const x0 = (prev + x(p.epoch)) / 2, x1 = (x(p.epoch) + next) / 2;
          return <rect key={p.epoch} x={i ? x0 : L} y={T} width={(i < points.length - 1 ? x1 : W - R) - (i ? x0 : L)} height={H - T - B}
            fill="transparent" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />;
        })}
        {h && <line x1={x(h.epoch)} x2={x(h.epoch)} y1={T} y2={H - B} stroke="var(--muted)" strokeWidth={1} strokeDasharray="3 3" pointerEvents="none" />}
      </svg>
      {h && (
        <div className="results-tip" data-testid="results-tip"
          style={{
            left: `${(x(h.epoch) / W) * 100}%`, top: `${(y(h.map50) / H) * 100}%`,
            // Kept inside the panel: anchored by its right edge near the right end, its left edge near the left.
            transform: `translate(${x(h.epoch) > W * 0.7 ? "-100%" : x(h.epoch) < W * 0.3 ? "0" : "-50%"}, calc(-100% - 10px))`,
          }}>
          <b>Epoch {h.epoch}</b><br />mAP50 {h.map50.toFixed(3)}{h.map50_95 !== null && <><br />mAP50-95 {h.map50_95.toFixed(3)}</>}
          {h === best && <><br />best: this is the saved model</>}
        </div>
      )}
    </div>
  );
}

/**
 * Validation results under the Train page's log: how the score developed, how each class did at the best epoch,
 * and what to do next. Shown once the run has been validated (every 5 epochs and after the last).
 */
export function ValidationResults({ metrics }: { metrics: TrainingMetrics | null }) {
  const r = summarizeResults(metrics);
  if (!r) return null;
  return (
    <div className="results" data-testid="results">
      <div className="results-head">
        <span style={{ fontWeight: 600 }}>Validation results</span>
        <span className="muted">best mAP50 <b style={{ color: "var(--text)" }}>{r.best.map50.toFixed(2)}</b> at epoch {r.best.epoch}
          {r.best.map50_95 !== null && <> · mAP50-95 {r.best.map50_95.toFixed(2)}</>}</span>
      </div>
      <div data-testid="results-verdict" style={{ margin: "6px 0" }}><BandTag b={r.band} /> {r.verdict.replace(/^[^:]+: /, "")}</div>
      {r.advice.map((a) => <div key={a} className="muted" data-testid="results-advice">• {a}</div>)}
      <div className="muted" style={{ marginTop: 8 }}>Validation mAP50 by epoch (0 to 1, higher is better)</div>
      <ScoreChart {...r} />
      {r.classes.length > 0 && (
        <table className="results-classes" data-testid="results-classes">
          <thead><tr><th>Class</th><th>AP50 at epoch {r.best.epoch}</th><th /></tr></thead>
          <tbody>
            {r.classes.map((c) => (
              <tr key={c.name}>
                <td>{c.name}</td>
                <td style={{ width: "50%" }}>
                  <div className="results-bar"><i style={{ width: `${Math.max(1, c.ap50 * 100)}%` }} /><span>{c.ap50.toFixed(2)}</span></div>
                </td>
                <td><BandTag b={c.band} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
